import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button } from '../button';
import { mediaQueryMin } from '../../theme';
import { colors } from '../../theme/color';
import CachedIcon from '@mui/icons-material/Cached';

const VersionCheck: React.FC = () => {
  const { isLatestVersion, isHidden, latestVersion, localVersion } = useVersionCheck();

  if (isHidden) return null;
  if (isLatestVersion) return null;

  const update = (): void => {
    window.location.reload();
  };

  return (
    <Container>
      <UpdateButton
        onClick={update}
        aria-label="Lataa sivu uudestaan saadaksesi uusimmat päivitykset"
        data-latest-version={latestVersion}
        data-local-version={localVersion}
      >
        <CachedIcon />
        <div>
          <span>Päivitimme juuri sivustoa.</span>
          <br />
          <strong>Lataa sivu uudelleen</strong>
        </div>
      </UpdateButton>
    </Container>
  );
};

export default VersionCheck;

const UpdateButton = styled(Button)`
  background-color: ${colors.green.lighter(70)};
  border-color: ${colors.green.lighter(70)};
  color: ${colors.green.darker(60)};
  font-weight: normal;
  box-shadow: 1px 1px 4px 0px rgba(0, 0, 0, 0.25);

  &:hover {
    background-color: ${colors.green.lighter(60)};
    border-color: ${colors.green.lighter(60)};
    color: ${colors.green.darker(60)};
  }
`;

const Container = styled.div`
  position: fixed;
  z-index: 9999;
  left: 24px;
  top: 24px;

  @media ${mediaQueryMin.tablet} {
    top: auto;
    bottom: 24px;
  }

  @media ${mediaQueryMin.medium} {
    top: auto;
    bottom: 48px;
    left: 48px;
  }
`;

type UseVersionResult = {
  isLatestVersion: boolean;
  isHidden: boolean;
  latestVersion: string;
  localVersion: string;
};

const useVersionCheck = (): UseVersionResult => {
  const response = useQuery({
    queryKey: ['kotipizza.version'],
    queryFn: async () => {
      try {
        const response = await fetch('/version');
        const version = await response.text();
        return version;
      } catch (error) {
        // we can silently fail in here.
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: true,
    retry: false,
    staleTime: 30000,
  });

  const localVersion = String(process.env.NEXT_PUBLIC_APP_VERSION || '').trim();
  const latestVersion = String(response.data || '').trim();

  return {
    isLatestVersion: localVersion === latestVersion,
    isHidden: response.isLoading || response.isError,
    latestVersion: latestVersion,
    localVersion: localVersion,
  };
};
